import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { apiKeysApi } from "../../../services/api/apiKeys";
import Icon from "../../../components/ui/Icon";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function StatusBadge({ isActive, expiresOn }) {
  const expired = expiresOn && new Date(expiresOn) < new Date();
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
        Expired
      </span>
    );
  }
  return isActive ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
      Revoked
    </span>
  );
}

// ─── Create Key Modal ─────────────────────────────────────────────────────────

function CreateKeyModal({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiKeysApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        expiresOn: expiresOn || undefined,
      });
      onCreate(result.data?.data ?? result.data);
    } catch (err) {
      setError(err?.response?.data?.message ?? err.message ?? "Failed to create key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Create API Key</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. POS Integration"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              maxLength={100}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description or usage notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              rows={2}
              maxLength={500}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires On{" "}
              <span className="text-gray-400 text-xs font-normal">(optional)</span>
            </label>
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank for a key that never expires.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              {loading && <Icon name="Loader2" size={14} className="animate-spin" />}
              Create Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Show Key Modal (one-time reveal) ────────────────────────────────────────

function ShowKeyModal({ keyValue, keyName, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(keyValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">API Key Created</h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Warning banner */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Copy this key now.</strong> For security reasons, the full API key is only
              shown once and cannot be retrieved again. Store it somewhere safe.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
              {keyName}
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 block px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800 break-all select-all">
                {keyValue}
              </code>
              <button
                onClick={handleCopy}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name={copied ? "Check" : "Copy"} size={15} />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            Use this key in the <code className="bg-gray-100 px-1 rounded">X-Api-Key</code> header
            when making requests to the API.
          </p>

          <div className="flex justify-end pt-1">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ApiKeysSettings() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null); // { key, name }
  const [revoking, setRevoking] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiKeysApi.getAll();
      setKeys(res.data?.data ?? res.data ?? []);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Failed to load API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreated = (data) => {
    setShowCreateModal(false);
    setNewKeyData({ key: data.key, name: data.name });
    fetchKeys();
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this API key? It will stop working immediately.")) return;
    setRevoking(id);
    try {
      await apiKeysApi.revoke(id);
      fetchKeys();
    } catch (err) {
      alert(err?.response?.data?.message ?? "Failed to revoke key.");
    } finally {
      setRevoking(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this API key? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await apiKeysApi.delete(id);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      alert(err?.response?.data?.message ?? "Failed to delete key.");
    } finally {
      setDeleting(null);
    }
  };

  const isAdmin = user?.role === "TenantAdmin" || user?.role === "GlobalAdmin";

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-sm text-gray-500 mt-1">
            Generate API keys to authenticate against the Rocket TroncManager API from external
            systems such as your POS, payroll software, or custom integrations.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={16} />
            New API Key
          </button>
        )}
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
        <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          Pass your API key in the{" "}
          <code className="bg-blue-100 px-1 py-0.5 rounded text-xs font-mono">X-Api-Key</code>{" "}
          header with every request. View the full API reference at{" "}
          <a
            href="/swagger"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-blue-900"
          >
            /swagger
          </a>
          .
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span className="text-sm">Loading API keys…</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 gap-3 text-red-500">
            <Icon name="AlertCircle" size={20} />
            <span className="text-sm">{error}</span>
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Icon name="KeyRound" size={36} strokeWidth={1.2} />
            <div className="text-center">
              <p className="font-medium text-gray-600">No API keys yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create your first key to start integrating with external systems.
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={15} />
                Create API Key
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">
                  Name
                </th>
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Key Prefix
                </th>
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">
                  Created
                </th>
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">
                  Last Used
                </th>
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell">
                  Expires
                </th>
                <th className="text-left px-5 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">
                  Status
                </th>
                {isAdmin && (
                  <th className="px-5 py-3" />
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {keys.map((k) => (
                <tr key={k.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-gray-900">{k.name}</div>
                    {k.description && (
                      <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{k.description}</div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-600">
                      {k.keyPrefix}…
                    </code>
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">
                    <div>{formatDate(k.createdOn)}</div>
                    {k.createdBy && (
                      <div className="text-xs text-gray-400">{k.createdBy}</div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">
                    {formatDate(k.lastUsedOn)}
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden lg:table-cell">
                    {formatDate(k.expiresOn)}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge isActive={k.isActive} expiresOn={k.expiresOn} />
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {k.isActive && (
                          <button
                            onClick={() => handleRevoke(k.id)}
                            disabled={revoking === k.id}
                            title="Revoke key"
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40"
                          >
                            {revoking === k.id ? (
                              <Icon name="Loader2" size={15} className="animate-spin" />
                            ) : (
                              <Icon name="ShieldOff" size={15} />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(k.id)}
                          disabled={deleting === k.id}
                          title="Delete key"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                        >
                          {deleting === k.id ? (
                            <Icon name="Loader2" size={15} className="animate-spin" />
                          ) : (
                            <Icon name="Trash2" size={15} />
                          )}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateKeyModal onClose={() => setShowCreateModal(false)} onCreate={handleCreated} />
      )}
      {newKeyData && (
        <ShowKeyModal
          keyValue={newKeyData.key}
          keyName={newKeyData.name}
          onClose={() => setNewKeyData(null)}
        />
      )}
    </div>
  );
}
